import React, { useState } from 'react';
import { Photo, Overlay, OverlayConfig } from '../../types';
import { photosApi } from '../../api/photos';
import { denyAction, isPermissionError } from '../../store/permissionDeniedStore';
import { useEditorStore } from '../../store/editorStore';
import { track, EVENTS } from '../../lib/mixpanel';
import { AlbumPhotoCell } from '../Albums/AlbumPhotoCell';

interface PhotoCardProps {
  photo: Photo;
  overlays: Overlay[];
  onDelete: (id: string) => void;
  /** When false, attempting delete shows a "no access" modal instead. */
  canDelete?: boolean;
  onHighlightChange?: (id: string, isHighlight: boolean) => void;
  onOpen?: () => void;
  selectMode?: boolean;
  selected?: boolean;
  onToggleSelect?: (id: string, shiftKey?: boolean) => void;
  onEnterSelectMode?: (id: string) => void;
  /** Accepted for API compatibility; AlbumPhotoCell is always flush-square. */
  compact?: boolean;
  defaultOverlayConfig?: OverlayConfig | null;
}

/**
 * Creator-gallery cell. Thin container that owns the creator business logic
 * (delete confirm + API, highlight toggle, open editor, deleting state) and
 * renders the shared <AlbumPhotoCell>. Viewer-only features (like, pin, report,
 * share, Google Photos sync) are intentionally not wired here.
 */
export const PhotoCard: React.FC<PhotoCardProps> = ({
  photo,
  overlays,
  onDelete,
  canDelete = true,
  onHighlightChange,
  onOpen,
  selectMode = false,
  selected = false,
  onToggleSelect,
  onEnterSelectMode,
  defaultOverlayConfig,
}) => {
  const [deleting, setDeleting] = useState(false);
  const { openEditor } = useEditorStore();

  const handleDelete = async () => {
    // Managers without delete permission get a clear "no access" modal up front.
    if (!canDelete) {
      denyAction('delete this photo');
      return;
    }
    if (!confirm('Delete this photo? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await photosApi.delete(photo.eventId, photo._id);
      onDelete(photo._id);
    } catch (err) {
      setDeleting(false);
      // Defense in depth: surface a server-side permission rejection clearly.
      if (isPermissionError(err)) denyAction('delete this photo');
    }
  };

  const handleToggleHighlight = () => {
    // Optimistic both ways — flip instantly; revert only on error.
    const next = !(photo.isHighlight ?? false);
    onHighlightChange?.(photo._id, next);
    photosApi.toggleHighlight(photo.eventId, photo._id).catch(() => {
      onHighlightChange?.(photo._id, !next); // revert on failure
    });
  };

  const handleEdit = () => {
    track(EVENTS.EDITOR_OPENED, {
      event_id: photo.eventId,
      photo_id: photo._id,
      photo_filename: photo.originalFilename,
    });
    openEditor(photo, overlays, onHighlightChange, defaultOverlayConfig);
  };

  return (
    <AlbumPhotoCell
      photo={photo}
      photoIndex={0}
      isSelected={selected}
      selectMode={selectMode}
      onSelect={(_id, shiftKey) => onToggleSelect?.(photo._id, shiftKey)}
      onView={() => onOpen?.()}
      onBadgeClick={() => onEnterSelectMode?.(photo._id)}
      onEdit={handleEdit}
      onDelete={handleDelete}
      isHighlight={photo.isHighlight ?? false}
      onToggleHighlight={handleToggleHighlight}
      isDeleting={deleting}
      useClientCompositor={false}
    />
  );
};
